import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const faqData = [
  {
    question: "What is BetBond?",
    answer:
      "BetBond is mobile software for **peer-to-peer matching** and **community tips**: you propose clear terms, another person accepts, funds sit in **escrow** while the outcome is determined, and settlement follows the rules you both saw before confirming. Tips let you read and share calls on fixtures before you match. We charge a **transparent service fee** — we are not taking the other side of your activity.",
  },
  {
    question: 'What does "peer-to-peer" mean here?',
    answer:
      "You are agreeing **with another participant**, not with BetBond as a counterparty. The app helps you **find a match**, **lock agreed terms**, and **move money through escrow** so completion is orderly. BetBond earns when facilitation succeeds (see the **7%** fee below), not from your side “winning” or “losing” against us.",
  },
  {
    question: "How do escrow and settlement work?",
    answer:
      "When both sides confirm, stakes are committed per the flow you chose. Funds are held according to **in-product rules** until the outcome is settled; releases and refunds follow those same rules — including void or edge cases — so you know what to expect before you tap confirm.",
  },
  {
    question: "What is the 7% fee?",
    answer:
      "Our headline **7%** applies on **eligible completed flows** where the product says it applies. The **exact amount** is shown **before you confirm** each time. We don’t hide it in the line: if something else applies (promos, partial refunds, a specific flow type), the confirmation screen is the source of truth.",
  },
  {
    question: "Can I make money on BetBond without betting?",
    answer:
      "Yes. Beyond placing P2P offers, you can earn by **inviting friends** through referrals and collecting **rewards from the weekly Arena leaderboard**. Availability is always shown in the app.",
  },
  {
    question: "What are Tips?",
    answer:
      "**Tips** are community calls on upcoming fixtures — read what others think, share your own pick, and use that signal when you post or join a **P2P** offer. Publishing a tip costs **100 COIN**. Tips are not a bet against BetBond; they sit next to matching so you can decide with more context.",
  },
  {
    question: "What can I do in the app today?",
    answer:
      "**P2P matching** and **community Tips** are the starting pair. Browse or post **P2P offers** on fixtures, **invite or join** counterparties, read or share **tips** (**100 COIN** to publish), fund from your **wallet**, and track status through escrow to settlement. Climb the **weekly Arena**. What you can create, stake, or discover may vary by region — availability and limits are always **shown in the app**.",
  },
  {
    question: "How do wallets and currencies work?",
    answer:
      "Your wallet has **Cash** (real money) and **Coin** (in-app). You can add Cash, convert **Cash ↔ Coin** where the app allows it, and use each balance where the product supports it. Fees, conversions, holds, or top-up methods appear on the screens where you add or withdraw — use those flows for authoritative detail.",
  },
  {
    question: "How are outcomes and disagreements resolved?",
    answer:
      "Settlement follows the **datasets and logic** wired into each outcome type wherever possible so results are predictable. Unusual cases may go through **in-app support** review as described there. Where rules say **void**, refunds follow **in-product** policy.",
  },
  {
    question: "Who can use BetBond, and where is it from?",
    answer:
      "BetBond is built in **Rwanda** for compliant **participant-to-participant infrastructure**. **18+ only**. Local law, geography, eligibility checks, and store rules still decide who may install, fund, or continue using the product — the app will tell you if something is restricted.",
  },
  {
    question: "How do I get help or report a concern?",
    answer:
      "Use **Contact** on the website or **Help** in the app, or email **betbondofficial@gmail.com**. For **privacy** requests, follow the process in our **Privacy Policy** so we can verify and respond properly.",
  },
];

/** Render simple **bold** segments in FAQ answers without markdown libs */
const AnswerText = ({ text }) => {
  const parts = text.split("**");
  return (
    <span className="text-gray-400 leading-relaxed">
      {parts.map((chunk, idx) =>
        idx % 2 === 1 ? (
          <strong key={idx} className="text-gray-300 font-semibold">
            {chunk}
          </strong>
        ) : (
          chunk
        )
      )}
    </span>
  );
};

const FaqItem = ({ question, answer, isOpen, toggle }) => {
  return (
    <div className="border-b border-white/10">
      <button
        type="button"
        className="w-full flex items-center justify-between py-4 text-left group focus:outline-none"
        onClick={toggle}
      >
        <div className="flex items-start space-x-3 pr-3">
          <div
            className={`
              w-3 h-3 rounded-full shrink-0 mt-1 transition-colors duration-300
              ${isOpen ? "bg-blue-500" : "bg-white/20"}
              group-hover:bg-blue-400
            `}
          />
          <h3
            className={`
              text-lg font-medium transition-colors duration-300
              ${isOpen ? "text-blue-400" : "text-white"}
              group-hover:text-blue-400
            `}
          >
            {question}
          </h3>
        </div>
        <ChevronDown
          size={20}
          className={`
            text-blue-400 shrink-0 transition-transform duration-300
            ${isOpen ? "rotate-180" : "rotate-0"}
          `}
        />
      </button>
      <div
        className={`
          overflow-hidden transition-all duration-500
          ${isOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"}
        `}
      >
        <p className="pb-5 pl-6 pr-4">
          <AnswerText text={answer} />
        </p>
      </div>
    </div>
  );
};

const FaqSection = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black px-4 md:px-6 py-24 md:py-32">
      <div className="container mx-auto max-w-3xl">
        <h2
          className={`
            text-3xl md:text-4xl font-extrabold text-center mb-10 text-white
            transform transition-all duration-700 delay-200
            ${mounted ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}
          `}
        >
          Frequently asked questions
        </h2>

        <div
          className={`
            transform transition-all duration-700 delay-400
            ${mounted ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}
          `}
        >
          {faqData.map((faq, index) => (
            <FaqItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openFaqIndex === index}
              toggle={() =>
                setOpenFaqIndex(openFaqIndex === index ? null : index)
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
